// frontend_react/src/components/paymodal/useCheckoutState.js

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ALL_VENTILATION_MODES, PAYMENT_METHODS, roundCents } from './constants';
import {
    processReceiptPayment,
    fetchReceiptLines,
    fetchReceiptPayments,
    applyReceiptDiscount,
    fetchReceiptFull,
    sendReceiptPdf,
    normalizeApiPayments,
    PAYMENT_METHOD_API_MAP,
} from './api';
import { useAdminScope } from "../../context/AdminScopeContext";

const DEMO_ITEMS = [
    { id: 1, name: 'Skyjo',                      price: 16.00, qty: 2, discounts: [] },
    { id: 2, name: 'Minecraft Builders & Biomes', price: 40.00, qty: 1, discounts: [] },
    { id: 3, name: 'Aube',                        price: 22.00, qty: 1, discounts: [] },
];

export const useCheckoutState = ({
    receiptUuid  = null,
    initialItems = null,
    onPaid       = null,
    pdfFormat    = 'A4',
    checkFormat  = true,
} = {}) => {

    const isApiMode = !!receiptUuid;
    const { selectedCompanyUuid } = useAdminScope();
    const [step,     setStep]    = useState(1);
    const [loading,  setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Articles viennent du panier (initialItems) — jamais chargés depuis l'API
    const [orderItems,      setOrderItems]      = useState(() => initialItems ?? DEMO_ITEMS);
    const [globalDiscounts, setGlobalDiscounts] = useState([]);

    // Lignes backend : [{ uuid, designation, amount_including_tax, ... }]
    // Chargées une fois via fetchReceiptLines pour les remises par article
    const [receiptLines, setReceiptLines] = useState([]);

    useEffect(() => {
        if (initialItems && initialItems.length > 0) setOrderItems(initialItems);
    }, [initialItems]);

    const [ventilationMode,   setVentilationMode]   = useState('total');
    const [splitCount,        setSplitCount]        = useState(2);
    const [splitBaseAmount,   setSplitBaseAmount]   = useState(null);
    const [splitPaymentsMade, setSplitPaymentsMade] = useState(0);
    const [freeAmount,        setFreeAmount]        = useState('');
    const [partialQty,        setPartialQty]        = useState({});
    const [paidItemQtys,      setPaidItemQtys]      = useState({});

    const [paymentHistory,    setPaymentHistory]    = useState([]);
    const [selectedMethod,    setSelectedMethod]    = useState(null);
    const [printReceipt,      setPrintReceipt]      = useState(false);
    const [ventilationTarget, setVentilationTarget] = useState(0);
    const [processing,        setProcessing]        = useState(false);
    const [cashMeta,          setCashMeta]          = useState(null);
    const [cashModalOpen,     setCashModalOpen]     = useState(false);
    // Historique local des paiements espèces (cash_given/cash_change) — non stocké en DB
    const [cashPaymentLog,    setCashPaymentLog]    = useState([]);

    const [fullReceipt, setFullReceipt] = useState(null);
    const [pdfUrl,      setPdfUrl]      = useState(null);

    const [adminModalOpen,    setAdminModalOpen]    = useState(false);
    const [adminModalPurpose, setAdminModalPurpose] = useState(null);

    const loadedUuidRef = useRef(null);

    // Quand receiptUuid arrive : charger paiements existants + lignes backend
    useEffect(() => {
        if (!receiptUuid || loadedUuidRef.current === receiptUuid) return;
        loadedUuidRef.current = receiptUuid;
        setLoading(true);

        Promise.all([
            fetchReceiptPayments(
                receiptUuid,
                {
                    companyUuid: selectedCompanyUuid,
                }
            ).catch(() => null),
            fetchReceiptLines(
                receiptUuid,
                {
                    companyUuid: selectedCompanyUuid,
                }
            ).catch(() => null),
        ]).then(([paysRes, linesRes]) => {
            // Paiements — GET /payment retourne [] directement ou 204
            if (paysRes?.data) {
                const raw = Array.isArray(paysRes.data) ? paysRes.data : (paysRes.data?.payments ?? []);
                if (raw.length > 0) setPaymentHistory(normalizeApiPayments(raw));
            }
            // Lignes — GET /lines retourne [] directement
            if (linesRes?.data) {
                const raw = Array.isArray(linesRes.data) ? linesRes.data : (linesRes.data?.lines ?? []);
                setReceiptLines(raw);
            }
        }).finally(() => setLoading(false));
    }, [receiptUuid, selectedCompanyUuid]);

    // Résout l'UUID de ligne backend à partir du nom de l'article local.
    // Les lignes ont : uuid, designation (nom du produit côté backend)
    const resolveLineUuid = (itemName) => {
        if (!receiptLines.length) return null;
        const target = String(itemName ?? '').toLowerCase().trim();
        // SalesLine.serialize() retourne 'wording' (pas designation/label/name)
        const match  = receiptLines.find(l => {
            const label = String(l.wording ?? l.designation ?? l.label ?? l.name ?? '').toLowerCase().trim();
            return label === target;
        });
        return match?.uuid ?? null;
    };

    // ─── Totaux ───────────────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const subtotal = orderItems.reduce((s, item) => s + item.price * item.qty, 0);
        const itemDiscountSum = orderItems.reduce((s, item) =>
            s + (item.discounts?.reduce((d, disc) => d + disc.amount, 0) ?? 0)
        , 0);
        const globalDiscountSum = globalDiscounts.reduce((s, d) =>
            s + (d.type === '%' ? roundCents(subtotal * d.value / 100) : d.value)
        , 0);
        const totalPromos = roundCents(itemDiscountSum + globalDiscountSum);
        const totalTTC    = Math.max(0, roundCents(subtotal - totalPromos));
        return { subtotal, itemDiscountSum, globalDiscountSum, totalPromos, totalTTC };
    }, [orderItems, globalDiscounts]);

    const paidAmount      = roundCents(paymentHistory.reduce((s, p) => s + p.amount, 0));
    const remainingAmount = Math.max(0, roundCents(totals.totalTTC - paidAmount));
    const availableVentModes = ALL_VENTILATION_MODES;
    const closeLocked        = paymentHistory.length > 0 && step < 4;

    const unpaidItems = orderItems
        .filter(item => (paidItemQtys[item.id] || 0) < item.qty)
        .map(item => ({ ...item, qty: item.qty - (paidItemQtys[item.id] || 0) }));

    const itemCurrentDiscount = (item) =>
        item.discounts?.reduce((s, d) => s + d.amount, 0) ?? 0;

    // ─── Handlers étape 1 ─────────────────────────────────────────────────────

    const handleUpdateQty = (id, delta) => {
        if (isApiMode) return;
        setOrderItems(items =>
            items
                .map(item => item.id === id ? { ...item, qty: Math.max(0, item.qty + delta) } : item)
                .filter(item => item.qty > 0)
        );
    };

    const handleRemoveItemDiscount = (itemId, discountId) => {
        setOrderItems(items => items.map(item =>
            item.id === itemId
                ? { ...item, discounts: item.discounts.filter(d => d.id !== discountId) }
                : item
        ));
    };

    const handleRemoveGlobalDiscount = (discountId) => {
        setGlobalDiscounts(prev => prev.filter(d => d.id !== discountId));
    };

    // Remise — payload identique à l'ancien DiscountModal :
    // { scope: 'cart'|'line', type: 'percent'|'amount', value: number, line_uuid }
    const handleApplyDiscount = async ({ scope, type, value, line_uuid }) => {
        if (!value || value <= 0) return;

        if (isApiMode) {
            setProcessing(true);
            setApiError(null);
            try {
                // Vérifie qu'on a des lignes chargées (nécessaire pour _recompute côté backend)
                if (!receiptLines.length) {
                    throw new Error('Les lignes du ticket ne sont pas encore chargées. Patientez un instant.');
                }
                // Le backend retourne { receipt, lines, totals } — source de vérité absolue
                const res = await applyReceiptDiscount(
                    receiptUuid,
                    { scope, type, value, line_uuid },
                    {
                        companyUuid: selectedCompanyUuid,
                    }
                );
                const updatedLines   = res?.data?.lines   ?? [];
                const updatedReceipt = res?.data?.receipt ?? null;
                const label = type === 'percent' ? `${value}%` : `${value}€`;

                // Sync orderItems depuis la réponse backend :
                // Pour chaque item, on cherche la ligne correspondante par wording (= item.name)
                // et on remplace item.discounts par la différence prix original - net backend.
                // item.price reste ORIGINAL — discounts porte la réduction.
                // Ainsi totals.totalTTC (= subtotal - itemDiscountSum) = vrai total backend.
                if (updatedLines.length > 0) {
                    setOrderItems(prev => prev.map(item => {
                        const backendLine = updatedLines.find(l =>
                            String(l.wording ?? '').toLowerCase().trim() ===
                            String(item.name ?? '').toLowerCase().trim()
                        );
                        if (!backendLine) return item;

                        const originalTotal = roundCents(item.price * item.qty);
                        const netTotal      = Number(backendLine.amount_including_tax ?? originalTotal);
                        const totalDiscount = roundCents(originalTotal - netTotal);

                        return {
                            ...item,
                            discounts: totalDiscount > 0
                                ? [{ id: `backend_${item.id}`, label: 'Remises', amount: totalDiscount }]
                                : [],
                        };
                    }));
                }

                // Pour remise panier : globalDiscounts = ce que le backend a calculé globalement
                if (scope === 'cart' && updatedReceipt) {
                    const subtotal  = orderItems.reduce((s, i) => s + i.price * i.qty, 0);
                    const netTotal  = Number(updatedReceipt.amount_including_tax ?? subtotal);
                    const lineDiscs = updatedLines.reduce((s, l) => {
                        const item = orderItems.find(i =>
                            String(i.name ?? '').toLowerCase().trim() ===
                            String(l.wording ?? '').toLowerCase().trim()
                        );
                        if (!item) return s;
                        return s + roundCents(item.price * item.qty - Number(l.amount_including_tax ?? item.price * item.qty));
                    }, 0);
                    const globalDisc = roundCents(subtotal - netTotal - lineDiscs);
                    setGlobalDiscounts(globalDisc > 0
                        ? [{ id: Date.now(), label, type: '€', value: globalDisc }]
                        : []
                    );
                }
            } catch (e) {
                setApiError(`Remise non appliquée : ${e.message}`);
            } finally {
                setProcessing(false);
            }
        } else {
            // Mode local
            const label = type === 'percent' ? `${value}%` : `${value}€`;
            if (scope === 'cart') {
                const subtotal       = orderItems.reduce((s, i) => s + i.price * i.qty, 0);
                const alreadyUsed    = orderItems.reduce((s, i) => s + itemCurrentDiscount(i), 0);
                const existingGlobal = globalDiscounts.reduce((s, d) => s + d.value, 0);
                const max        = Math.max(0, subtotal - alreadyUsed - existingGlobal);
                const rawAmount  = type === 'percent' ? roundCents(subtotal * value / 100) : value;
                const safeAmount = Math.min(rawAmount, max);
                if (safeAmount > 0)
                    setGlobalDiscounts(prev => [...prev, { id: Date.now(), label, type: '€', value: safeAmount }]);
            }
        }
    };

    // Alias pour compatibilité (plus utilisés directement, mais gardés pour OrderSummary)
    const handleApplyTicketTotal = (val, typeSymbol) =>
        handleApplyDiscount({
            scope:     'cart',
            type:      typeSymbol === '%' ? 'percent' : 'amount',
            value:     parseFloat(val),
            line_uuid: null,
        });

    const handleApplyItemDiscount = () => {}; // géré directement dans DiscountManager

    // ─── Calcul cible de ventilation ─────────────────────────────────────────

    const getVentilationTarget = () => {
        let target;
        switch (ventilationMode) {
            case 'total':   target = remainingAmount; break;
            case 'split': {
                if (splitBaseAmount != null) {
                    const paymentsLeft = splitCount - splitPaymentsMade;
                    target = paymentsLeft <= 1 ? remainingAmount : splitBaseAmount;
                } else {
                    target = roundCents(remainingAmount / splitCount);
                }
                break;
            }
            case 'free':    target = parseFloat(freeAmount) || 0; break;
            case 'partial':
                target = Object.entries(partialQty).reduce((sum, [id, qty]) => {
                    const item = orderItems.find(i => String(i.id) === String(id));
                    return item ? sum + roundCents(item.price * qty) : sum;
                }, 0);
                break;
            default: target = remainingAmount;
        }
        return Math.min(Math.max(0, roundCents(target)), remainingAmount);
    };

    // ─── Navigation ──────────────────────────────────────────────────────────

    const handleGoToStep = (s) => setStep(s);

    const handleNext = () => {
        if (step === 1) {
            if (totals.totalTTC <= 0) { setStep(4); return; }
            setStep(2);
        } else if (step === 2) {
            setVentilationTarget(getVentilationTarget());
            setSelectedMethod(null);
            setStep(3);
        }
    };

    const handleBack = () => { if (step === 3) setStep(2); };

    const handleSelectMethod = (methodId) => {
        if (methodId === selectedMethod) { setSelectedMethod(null); return; }
        if (methodId === 'cash') { setCashModalOpen(true); }
        else { setSelectedMethod(methodId); setCashMeta(null); }
    };

    const handleCashValidate = ({ cash_given, cash_change }) => {
        setCashModalOpen(false);
        setSelectedMethod('cash');
        setCashMeta({ cash_given, cash_change });
    };

    const handleCashClose = () => setCashModalOpen(false);

    // ─── Validation du paiement ───────────────────────────────────────────────
    // Identique à l'ancien PayModal : processReceiptPayment puis fetchReceiptPayments

    const handleValidatePayment = async () => {
        if (!selectedMethod) return;
        const target = ventilationTarget;

        if (isApiMode) {
            setProcessing(true);
            setApiError(null);
            try {
                const apiMethod = PAYMENT_METHOD_API_MAP[selectedMethod] ?? selectedMethod;

                // On envoie TOUJOURS ventilationTarget (montant dû), jamais cash_given.
                // Cela évite le 400 "dépassement" côté backend.
                // cash_given/cash_change sont gérés côté front uniquement (affichage ticket).
                await processReceiptPayment(
                    receiptUuid,
                    apiMethod,
                    target,
                    checkFormat,
                    {
                        companyUuid: selectedCompanyUuid,
                    }
                );

                // Mémoriser les infos espèces pour l'affichage du ticket
                if (selectedMethod === 'cash' && cashMeta) {
                    setCashPaymentLog(prev => [...prev, {
                        amount_due:   target,
                        cash_given:   cashMeta.cash_given,
                        cash_change:  cashMeta.cash_change,
                    }]);
                }

                // Re-sync paiements
                const paysRes = await fetchReceiptPayments(
                    receiptUuid,
                    {
                        companyUuid: selectedCompanyUuid,
                    }
                );
                const rawPays = Array.isArray(paysRes.data) ? paysRes.data : (paysRes.data?.payments ?? []);
                const newHistory = rawPays.length > 0
                    ? normalizeApiPayments(rawPays)
                    : [...paymentHistory, { id: Date.now(), label: selectedMethod, amount: target, mode: ventilationMode }];

                setPaymentHistory(newHistory);
                setSelectedMethod(null);
                setCashMeta(null);

                if (ventilationMode === 'split') {
                    setSplitPaymentsMade(n => n + 1);
                    if (splitBaseAmount == null)
                        setSplitBaseAmount(Math.floor(remainingAmount / splitCount * 100) / 100);
                }
                if (ventilationMode === 'partial') {
                    setPaidItemQtys(prev => {
                        const u = { ...prev };
                        for (const [id, qty] of Object.entries(partialQty)) u[id] = (u[id] || 0) + qty;
                        return u;
                    });
                }
                setPartialQty({});
                setFreeAmount('');

                const newPaid = roundCents(newHistory.reduce((s, p) => s + p.amount, 0));
                if (newPaid >= totals.totalTTC - 0.005) {
                    await finalizeReceipt();
                } else {
                    setStep(2);
                }

            } catch (e) {
                setApiError(`Paiement échoué : ${e.message}`);
            } finally {
                setProcessing(false);
            }
        } else {
            const method     = PAYMENT_METHODS.find(m => m.id === selectedMethod);
            const newHistory = [...paymentHistory, {
                id: Date.now(), label: method?.label ?? selectedMethod, amount: target, mode: ventilationMode,
            }];
            setPaymentHistory(newHistory);
            setSelectedMethod(null);
            setCashMeta(null);
            if (ventilationMode === 'split') {
                setSplitPaymentsMade(n => n + 1);
                if (splitBaseAmount == null)
                    setSplitBaseAmount(Math.floor(remainingAmount / splitCount * 100) / 100);
            }
            if (ventilationMode === 'partial') {
                setPaidItemQtys(prev => {
                    const u = { ...prev };
                    for (const [id, qty] of Object.entries(partialQty))
                        u[Number(id)] = (u[Number(id)] || 0) + qty;
                    return u;
                });
            }
            setPartialQty({});
            setFreeAmount('');
            const newPaid = roundCents(newHistory.reduce((s, p) => s + p.amount, 0));
            setStep(newPaid >= totals.totalTTC - 0.005 ? 4 : 2);
        }
    };

    // ─── Finalisation ─────────────────────────────────────────────────────────
    // Identique à l'ancien handleCheckoutAction(withTicket=true)

    const finalizeReceipt = async () => {
        try {
            const res = await fetchReceiptFull(
                receiptUuid,
                {
                    companyUuid: selectedCompanyUuid,
                }
            );

            setFullReceipt(res.data ?? res);
        } catch (e) {
            setApiError(`Récupération ticket : ${e.message}`);
        }
        try {
            const pdfRes = await sendReceiptPdf(
                receiptUuid,
                pdfFormat,
                {
                    companyUuid: selectedCompanyUuid,
                }
            );
            const blob   = new Blob([pdfRes.data], { type: 'application/pdf' });
            setPdfUrl(URL.createObjectURL(blob));
        } catch { /* PDF optionnel */ }
        setStep(4);
    };

    // ─── Annulation admin ─────────────────────────────────────────────────────

    const handleRequestCancel = () => {
        if (closeLocked) { setAdminModalPurpose('cancel'); setAdminModalOpen(true); }
    };

    const handleAdminSuccess = () => {
        setAdminModalOpen(false); setAdminModalPurpose(null);
        setPaymentHistory([]); setSplitBaseAmount(null); setSplitPaymentsMade(0);
        setPaidItemQtys({}); setPartialQty({}); setFreeAmount('');
        setVentilationMode('total'); setSelectedMethod(null); setCashMeta(null);
        setCashPaymentLog([]);
        setStep(1);
    };

    const handleAdminClose = () => { setAdminModalOpen(false); setAdminModalPurpose(null); };

    // ─── Footer ───────────────────────────────────────────────────────────────

    const footerLabel = () => {
        if (step === 1) return 'SUIVANT :';
        if (step === 2) return 'SUIVANT :';
        if (step === 3) return 'VALIDER LE PAIEMENT';
        return null;
    };

    const footerAmount = () => {
        if (step === 1) return totals.totalTTC;
        if (step === 2) return getVentilationTarget();
        return null;
    };

    const isFooterDisabled =
        loading || processing ||
        (step === 3 && !selectedMethod) ||
        (step === 2 && getVentilationTarget() <= 0);

    const canGoBack = step === 3 && !processing;

    return {
        step, handleNext, handleBack, canGoBack,
        isFooterDisabled, footerLabel, footerAmount, handleGoToStep,
        isApiMode, loading, apiError,
        orderItems, globalDiscounts, totals,
        handleUpdateQty, handleRemoveItemDiscount,
        handleRemoveGlobalDiscount, handleApplyTicketTotal, handleApplyItemDiscount,
        handleApplyDiscount,
        ventilationMode, setVentilationMode, availableVentModes,
        splitCount, setSplitCount, splitBaseAmount, splitPaymentsMade,
        freeAmount, setFreeAmount,
        partialQty, setPartialQty, unpaidItems,
        paidAmount, remainingAmount, processing,
        paymentHistory, selectedMethod, handleSelectMethod,
        printReceipt, setPrintReceipt,
        ventilationTarget, handleValidatePayment,
        cashModalOpen, handleCashValidate, handleCashClose,
        cashPaymentLog,
        fullReceipt, pdfUrl,
        closeLocked, adminModalOpen, adminModalPurpose,
        handleRequestCancel, handleAdminSuccess, handleAdminClose,
        receiptLines, // exposé pour debug
    };
};